import type { ApprovalStatus } from '@shopgnd/shared';
import { getIdToken } from '@/lib/auth';
import { vendorIsApproved } from '@/lib/vendorApproval';
import {
  approvalStatusAfterOnboardingSubmit,
  shouldNotifyVendorApplicationSubmitted,
} from '@/lib/vendorOnboarding';

async function vendorNotifyUrl(): Promise<string | null> {
  const fromEnv = import.meta.env.VITE_VENDOR_NOTIFY_URL;
  if (fromEnv) return fromEnv;
  try {
    const cacheBust = import.meta.env.DEV ? `?t=${Date.now()}` : '';
    const res = await fetch(`/amplify_outputs.json${cacheBust}`, { cache: 'no-store' });
    if (res.ok) {
      const outputs = (await res.json()) as { custom?: { vendorNotifyUrl?: string } };
      return outputs.custom?.vendorNotifyUrl ?? null;
    }
  } catch {
    /* missing outputs */
  }
  return null;
}

async function postVendorNotify<T>(body: Record<string, unknown>): Promise<T> {
  const url = await vendorNotifyUrl();
  const token = await getIdToken();
  if (!url || !token) {
    throw new Error('Vendor notification API is not configured.');
  }

  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });

  let payload: T & { error?: string };
  try {
    payload = (await res.json()) as T & { error?: string };
  } catch {
    throw new Error(`Vendor notify failed (${res.status})`);
  }
  if (!res.ok) throw new Error(payload.error ?? 'Vendor notify failed');
  return payload as T;
}

/** Email the seller that their application was received and is pending review. */
export async function notifyVendorApplicationSubmitted(vendorId: string): Promise<void> {
  await postVendorNotify({ action: 'applicationSubmitted', vendorId });
}

export type BookingNotifyResult =
  | {
      kind: 'sent';
      vendorSent?: boolean;
      buyerSent?: boolean;
      whatsappUrl?: string;
      notifyChannels?: Array<'email' | 'whatsapp'>;
    }
  | { kind: 'failed'; message: string };

/** Email vendor and buyer after a booking request is submitted. */
export async function notifyBookingRequest(appointmentId: string): Promise<BookingNotifyResult> {
  try {
    const res = await postVendorNotify<{
      sent?: boolean;
      vendorSent?: boolean;
      buyerSent?: boolean;
      whatsappUrl?: string;
      notifyChannels?: Array<'email' | 'whatsapp'>;
      error?: string;
    }>({ action: 'bookingRequest', appointmentId });
    if (res.sent) {
      return {
        kind: 'sent',
        vendorSent: res.vendorSent,
        buyerSent: res.buyerSent,
        whatsappUrl: res.whatsappUrl,
        notifyChannels: res.notifyChannels,
      };
    }
    return { kind: 'failed', message: res.error ?? 'Booking notification was not sent.' };
  } catch (e) {
    return {
      kind: 'failed',
      message: e instanceof Error ? e.message : 'Could not send booking notification',
    };
  }
}

export type VendorApplicationNotifyResult =
  | { kind: 'sent' }
  | { kind: 'skipped' }
  | { kind: 'failed'; message: string };

/**
 * Calls vendor-notify only when the application newly enters (or re-enters) the review queue.
 * Requires `approvalStatus === 'pending'` on the vendor row (set before calling).
 */
export async function notifyVendorApplicationSubmittedAfterSubmit(
  vendorId: string,
  approvalStatus: ApprovalStatus | undefined,
  previousStatus: ApprovalStatus | undefined,
): Promise<VendorApplicationNotifyResult> {
  const expectedStatus = approvalStatusAfterOnboardingSubmit(previousStatus, false);
  if (vendorIsApproved(approvalStatus) || approvalStatus !== 'pending') {
    if (approvalStatus !== expectedStatus) {
      return {
        kind: 'failed',
        message: 'Application was saved but is not marked pending for review yet. Try again in a moment.',
      };
    }
    return { kind: 'skipped' };
  }
  if (!shouldNotifyVendorApplicationSubmitted(previousStatus)) {
    return { kind: 'skipped' };
  }
  try {
    await notifyVendorApplicationSubmitted(vendorId);
    return { kind: 'sent' };
  } catch (e) {
    return {
      kind: 'failed',
      message: e instanceof Error ? e.message : 'Could not send confirmation email',
    };
  }
}

export type VendorApprovalDecisionType = 'store' | 'cardPayments' | 'giftCertificates' | 'propertyListings';

export type VendorApprovalDecisionEmailResult =
  | { kind: 'sent' }
  | { kind: 'failed'; message: string };

/** Email the seller when an admin approves or rejects a vendor request. */
export async function notifyVendorApplicationDecided(params: {
  vendorId: string;
  status: 'approved' | 'rejected';
  note?: string;
  decisionType?: VendorApprovalDecisionType;
}): Promise<VendorApprovalDecisionEmailResult> {
  try {
    const res = await postVendorNotify<{ sent?: boolean; error?: string }>({
      action: 'applicationDecided',
      vendorId: params.vendorId,
      status: params.status,
      note: params.note,
      decisionType: params.decisionType ?? 'store',
    });
    if (res.sent) return { kind: 'sent' };
    return { kind: 'failed', message: res.error ?? 'Decision email was not sent.' };
  } catch (e) {
    return {
      kind: 'failed',
      message: e instanceof Error ? e.message : 'Could not send decision email',
    };
  }
}
